import React, { useState } from "react";
import { Tabs, Button, Drawer, Badge, Typography, Flex, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { showLoading, hideLoading } from "../redux/alertsSlice";
import { setUser } from "../redux/userSlice";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  BellOutlined, CheckOutlined, DeleteOutlined,
  ClockCircleOutlined, CheckCircleOutlined,
  MedicineBoxOutlined, InfoCircleOutlined
} from "@ant-design/icons";

const { Text} = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Style injection ────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("notif-styles")) return;
  const el = document.createElement("style");
  el.id = "notif-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    /* Bell trigger button */
    .notif-bell-btn {
      position: relative; width: 42px; height: 42px;
      border-radius: 12px !important; border: none !important;
      background: rgba(255,255,255,.12) !important;
      display: flex !important; align-items: center !important; justify-content: center !important;
      color: #fff !important; font-size: 18px !important;
      transition: background .2s, transform .2s !important;
      padding: 0 !important;
    }
    .notif-bell-btn:hover {
      background: rgba(255,255,255,.22) !important;
      transform: scale(1.05) !important;
    }

    /* Drawer overrides */
    .notif-drawer .ant-drawer-header {
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%) !important;
      border-bottom: none !important; padding: 20px 24px !important;
    }
    .notif-drawer .ant-drawer-title { color: ${WHITE} !important; font-family: 'Sora', sans-serif !important; font-weight: 700 !important; font-size: 16px !important; }
    .notif-drawer .ant-drawer-close { color: ${MUTED} !important; }
    .notif-drawer .ant-drawer-body { padding: 0 !important; background: ${SURFACE} !important; }

    /* Tabs inside drawer */
    .notif-drawer .ant-tabs-nav { padding: 0 20px !important; background: ${WHITE} !important; margin-bottom: 0 !important; box-shadow: 0 2px 8px rgba(0,0,0,.04); }
    .notif-drawer .ant-tabs-tab { font-family: 'DM Sans', sans-serif !important; font-weight: 600 !important; font-size: 13px !important; }
    .notif-drawer .ant-tabs-tab-active .ant-tabs-tab-btn { color: ${EMERALD} !important; }
    .notif-drawer .ant-tabs-ink-bar { background: ${EMERALD} !important; }
    .notif-drawer .ant-tabs-content-holder { padding: 16px 16px 0 !important; }

    /* Notification card */
    .notif-card {
      background: ${WHITE}; border-radius: 14px;
      border: 1.5px solid #e2e8f0;
      padding: 14px 16px; margin-bottom: 10px;
      cursor: pointer;
      transition: border-color .2s, box-shadow .2s, transform .15s;
      position: relative; overflow: hidden;
    }
    .notif-card:hover {
      border-color: ${TEAL};
      box-shadow: 0 6px 20px rgba(13,148,136,.12);
      transform: translateY(-1px);
    }
    .notif-card.unseen::before {
      content: ''; position: absolute; left: 0; top: 0; bottom: 0;
      width: 4px; background: linear-gradient(180deg, ${EMERALD}, ${TEAL});
      border-radius: 4px 0 0 4px;
    }
    .notif-card.seen { opacity: .85; }
    .notif-card.seen::before {
      content: ''; position: absolute; left: 0; top: 0; bottom: 0;
      width: 4px; background: #e2e8f0; border-radius: 4px 0 0 4px;
    }

    /* Action bar */
    .notif-action-bar {
      display: flex; justify-content: flex-end; align-items: center;
      padding: 10px 4px 12px;
    }
    .notif-action-btn {
      border-radius: 8px !important; height: 34px !important; font-size: 12px !important;
      font-weight: 600 !important; border: 1.5px solid #e2e8f0 !important;
      color: ${SLATE_MID} !important;
      transition: border-color .2s, color .2s !important;
    }
    .notif-action-btn:hover { border-color: ${TEAL} !important; color: ${EMERALD} !important; }
    .notif-action-btn.danger:hover { border-color: #ef4444 !important; color: #ef4444 !important; }

    /* Scroll area */
    .notif-scroll {
      max-height: calc(100vh - 200px);
      overflow-y: auto; padding-bottom: 20px;
    }
    .notif-scroll::-webkit-scrollbar { width: 4px; }
    .notif-scroll::-webkit-scrollbar-thumb { background: ${TEAL_LIGHT}; border-radius: 4px; }

    /* Empty state */
    .notif-empty { padding: 40px 20px; text-align: center; }

    @keyframes bellRing {
      0%, 100% { transform: rotate(0); }
      15%       { transform: rotate(15deg); }
      30%       { transform: rotate(-12deg); }
      45%       { transform: rotate(10deg); }
      60%       { transform: rotate(-8deg); }
      75%       { transform: rotate(5deg); }
    }
    .bell-ring { animation: bellRing .6s ease; }

    @keyframes slideIn {
      from { opacity: 0; transform: translateX(12px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    .notif-card { animation: slideIn .25s ease both; }
  `;
  document.head.appendChild(el);
};

/* ─── Time formatter ─────────────────────────────────────────── */
const formatTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

/* ─── Single notification card ───────────────────────────────── */
function NotifCard({ notification, seen = false, onClick }) {
  return (
    <div className={`notif-card ${seen ? "seen" : "unseen"}`} onClick={onClick}>
      <Flex gap={12} align="flex-start">
        <div style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: seen ? SURFACE : TEAL_LIGHT,
          border: `1px solid ${seen ? "#e2e8f0" : "#99f6e4"}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: seen ? MUTED : EMERALD, fontSize: 16,
        }}>
          <MedicineBoxOutlined />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontSize: 13, color: seen ? SLATE_MID : SLATE, display: "block", lineHeight: 1.5, fontWeight: seen ? 400 : 500 }}>
            {notification.message}
          </Text>
          {notification.createdAt && (
            <Text style={{ fontSize: 11, color: MUTED, marginTop: 4, display: "block" }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              {formatTime(notification.createdAt)}
            </Text>
          )}
        </div>
        {!seen && (
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: TEAL, flexShrink: 0, marginTop: 6 }} />
        )}
        {seen && (
          <CheckCircleOutlined style={{ color: "#d1fae5", fontSize: 14, flexShrink: 0, marginTop: 2 }} />
        )}
      </Flex>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────── */
const Notifications = () => {
  const [open, setOpen]           = useState(false);
  const [ringing, setRinging]     = useState(false);
  const dispatch                  = useDispatch();
  const { user }                  = useSelector((state) => state.user);
  const navigate                  = useNavigate();

  injectStyles();

  const handleBellClick = () => {
    setRinging(true);
    setTimeout(() => setRinging(false), 700);
    setOpen(true);
  };

  const unseenCount = user?.unseenNotifications?.length || 0;

  const markAllSeen = async () => {
    try {
      dispatch(showLoading());
      const res = await axios.post(
        "/api/user/mark-all-notifications-seen",
        { userId: user._id },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (res.data.success) { toast.success(res.data.message); dispatch(setUser(res.data.data)); }
      else toast.error(res.data.message);
    } catch { dispatch(hideLoading()); toast.error("Something went wrong"); }
  };

  const deleteAll = async () => {
    try {
      dispatch(showLoading());
      const res = await axios.post(
        "/api/user/delete-all-notifications",
        { userId: user._id },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (res.data.success) { toast.success(res.data.message); dispatch(setUser(res.data.data)); }
      else toast.error(res.data.message);
    } catch { dispatch(hideLoading()); toast.error("Something went wrong"); }
  };

  const handleNotifClick = () => {
    if (user?.isAdmin) navigate("/admin/doctorslist");
    else if (user?.isDoctor) navigate("/doctor/reviews");
    setOpen(false);
  };

  /* Drawer title with live count */
  const drawerTitle = (
    <Flex align="center" gap={10}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `${TEAL}33`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: TEAL, fontSize: 18,
      }}>
        <BellOutlined />
      </div>
      <div>
        <Text strong style={{ color: WHITE, fontFamily: "'Sora', sans-serif", fontSize: 15, display: "block", lineHeight: 1.2 }}>
          Notifications
        </Text>
        {unseenCount > 0 && (
          <Text style={{ color: TEAL, fontSize: 11, fontWeight: 600 }}>
            {unseenCount} unread
          </Text>
        )}
      </div>
    </Flex>
  );

  return (
    <>
      {/* Bell trigger */}
      <Badge
        count={unseenCount}
        size="small"
        offset={[-4, 4]}
        style={{ background: "#ef4444", boxShadow: "0 0 0 2px rgba(15,23,42,.6)" }}
      >
        <Button className={`notif-bell-btn ${ringing ? "bell-ring" : ""}`} onClick={handleBellClick}>
          <BellOutlined />
        </Button>
      </Badge>

      {/* Drawer */}
      <Drawer
        className="notif-drawer"
        title={drawerTitle}
        placement="right"
        width={400}
        onClose={() => setOpen(false)}
        open={open}
        closable
      >
        <Tabs
          defaultActiveKey="0"
          items={[
            {
              key: "0",
              label: (
                <Flex align="center" gap={6}>
                  <BellOutlined />
                  Unseen
                  {unseenCount > 0 && (
                    <Tag style={{ background: TEAL_LIGHT, color: EMERALD, border: "none", borderRadius: 20, fontSize: 10, fontWeight: 700, padding: "0 6px", lineHeight: "18px" }}>
                      {unseenCount}
                    </Tag>
                  )}
                </Flex>
              ),
              children: (
                <div>
                  {/* Action bar */}
                  {(user?.unseenNotifications?.length || 0) > 0 && (
                    <div className="notif-action-bar">
                      <Button className="notif-action-btn" icon={<CheckOutlined />} onClick={markAllSeen}>
                        Mark all as seen
                      </Button>
                    </div>
                  )}

                  {/* Notification list */}
                  <div className="notif-scroll">
                    {(user?.unseenNotifications?.length || 0) === 0 ? (
                      <div className="notif-empty">
                        <div style={{
                          width: 60, height: 60, borderRadius: "50%",
                          background: TEAL_LIGHT, margin: "0 auto 14px",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 26, color: EMERALD
                        }}>
                          <CheckCircleOutlined />
                        </div>
                        <Text strong style={{ display: "block", color: SLATE, marginBottom: 4 }}>All caught up!</Text>
                        <Text style={{ color: MUTED, fontSize: 13 }}>No new notifications right now.</Text>
                      </div>
                    ) : (
                      user.unseenNotifications.map((n, i) => (
                        <NotifCard
                          key={n._id || i}
                          notification={n}
                          seen={false}
                          onClick={handleNotifClick}
                        />
                      ))
                    )}
                  </div>
                </div>
              ),
            },
            {
              key: "1",
              label: (
                <Flex align="center" gap={6}>
                  <CheckCircleOutlined />
                  Seen
                  {(user?.seenNotifications?.length || 0) > 0 && (
                    <Tag style={{ background: SURFACE, color: MUTED, border: "1px solid #e2e8f0", borderRadius: 20, fontSize: 10, fontWeight: 700, padding: "0 6px", lineHeight: "18px" }}>
                      {user.seenNotifications.length}
                    </Tag>
                  )}
                </Flex>
              ),
              children: (
                <div>
                  {/* Action bar */}
                  {(user?.seenNotifications?.length || 0) > 0 && (
                    <div className="notif-action-bar">
                      <Button className="notif-action-btn danger" icon={<DeleteOutlined />} onClick={deleteAll}>
                        Delete All
                      </Button>
                    </div>
                  )}

                  {/* Notification list */}
                  <div className="notif-scroll">
                    {(user?.seenNotifications?.length || 0) === 0 ? (
                      <div className="notif-empty">
                        <div style={{
                          width: 60, height: 60, borderRadius: "50%",
                          background: SURFACE, border: "1px solid #e2e8f0",
                          margin: "0 auto 14px",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 26, color: MUTED
                        }}>
                          <InfoCircleOutlined />
                        </div>
                        <Text strong style={{ display: "block", color: SLATE, marginBottom: 4 }}>Nothing here yet</Text>
                        <Text style={{ color: MUTED, fontSize: 13 }}>Seen notifications will appear here.</Text>
                      </div>
                    ) : (
                      user.seenNotifications.map((n, i) => (
                        <NotifCard
                          key={n._id || i}
                          notification={n}
                          seen={true}
                          onClick={handleNotifClick}
                        />
                      ))
                    )}
                  </div>
                </div>
              ),
            },
          ]}
        />
      </Drawer>
    </>
  );
};

export default Notifications;